import type { CSSProperties } from 'react';
import React, { useState } from 'react';
import cn from 'classnames';
import { Stack } from '@alfa-bank/site-stack';
import { IconViewSuperEllipse } from '@alfa-bank/site-icon-view/super-ellipse';
import type { TCircle } from '@alfa-bank/site-icon-view/src/models/index';
import { useDebounce } from 'react-use';

import type { TUseTabHeaderResult } from '../hooks/use-tabs-header';
import { DEFAULT_BROWSER } from '../../../config';
import type { IHeaderPrivateProps, IHeaderPublicProps } from '../../../../models';
import type { ITabsHeaderPublicProps, ITabsPrivateProps } from '../../../tabs/models';
import type { TTabsPanelHeaderProps } from '../../panel-header/models';
import type stylesTabsHeaders from '../styles/tabs-headers.css';
import type stylesPanelHeaderMobile from '../../panel-header/mobile/styles/styles.css';
import type stylesPanelHeaderV2Desktop from '../../panel-header-v2/desktop/styles/styles.css';
import { ANIMATION_DURATION_MS } from '../config';

export type TRenderedHeader = Pick<IHeaderPrivateProps, 'dataTestId' | 'Text' | 'progress'> &
    IHeaderPublicProps &
    (ITabsHeaderPublicProps | TTabsPanelHeaderProps) &
    TUseTabHeaderResult &
    Pick<ITabsPrivateProps, 'activeTabsCustomColors'> & {
        className?: string;
        /** Пропс-флаг, добавляющий стили для виджета 'Tabs.TabsPanelV2' когда в него передается пропс `widthTab = 'equal'` */
        equalTabWidth?: boolean;
        iconBackgroundColor?: CSSProperties['backgroundColor'];
        iconSize?: TCircle['size'];
        isMobile?: boolean;
        styles:
            | typeof stylesPanelHeaderV2Desktop
            | typeof stylesTabsHeaders
            | typeof stylesPanelHeaderMobile;
        tabInlineStyle?: CSSProperties;
    };

export const RenderedHeader = ({
    Text,
    activeTabsCustomColors = {},
    progress,
    alignStyle,
    browser = DEFAULT_BROWSER,
    className,
    equalTabWidth,
    dataTestId,
    headerContainer,
    headerList,
    iconBackgroundColor,
    linePosition,
    lineWidth,
    iconSize = 32,
    isMobile = false,
    onClick,
    onTouchEnd,
    onTouchStart,
    selectedID,
    selectedTab,
    styles,
    tabInlineStyle,
    themeStyle,
    titles,
    translateX,
}: TRenderedHeader) => {
    const [debouncedSelectedId, setDebouncedSelectedId] = useState<typeof selectedID>(selectedID);

    useDebounce(
        () => {
            setDebouncedSelectedId(selectedID);
        },
        ANIMATION_DURATION_MS,
        [selectedID]
    );

    const equalWidthStyles = 'equalTabWidth' in styles ? styles.equalTabWidth : undefined;

    const hasCustomColors = !!Object.keys(activeTabsCustomColors).length;

    return (
        <div
            ref={headerContainer}
            className={cn(styles.headerContainer, styles[themeStyle], className, {
                [styles.customColors]: hasCustomColors,
            })}
            data-test-id={dataTestId}
            onTouchEnd={onTouchEnd}
            onTouchStart={onTouchStart}
        >
            <div
                ref={headerList}
                className={cn(styles.headerList, styles[alignStyle as keyof typeof styles])}
                role="tablist"
                style={{
                    transform: `translateX(${translateX}px)`,
                }}
            >
                {selectedID !== debouncedSelectedId && (
                    <Stack>
                        {zIndex => (
                            <>
                                <div
                                    className={styles.indicator}
                                    data-test-id="indicator"
                                    style={{
                                        zIndex,
                                        transform: `translateX(${linePosition}px)`,
                                        width: lineWidth,
                                    }}
                                />
                            </>
                        )}
                    </Stack>
                )}

                <Stack>
                    {/* eslint-disable-next-line sonarjs/cognitive-complexity*/}
                    {zIndex => (
                        <>
                            {titles.map(item => {
                                const customActiveTab =
                                    activeTabsCustomColors[item.id] && item.id === selectedID
                                        ? activeTabsCustomColors[selectedID]
                                        : null;

                                return (
                                    <div
                                        key={item.id}
                                        ref={selectedID === item.id ? selectedTab : null}
                                        aria-selected={selectedID === item.id}
                                        className={cn(styles.header, {
                                            [styles.selected]:
                                                debouncedSelectedId === item.id &&
                                                selectedID === debouncedSelectedId,
                                            [styles.mobile]: browser.mobile,
                                            [styles.customActiveTabBgColor]: !!customActiveTab,
                                            ...(equalWidthStyles && {
                                                [equalWidthStyles]: equalTabWidth,
                                            }),
                                        })}
                                        data-test-id={`${dataTestId}-${item.id}`}
                                        onClick={event =>
                                            onClick(
                                                event,
                                                item.id,
                                                item.analytics?.click || item.dataLayer
                                            )
                                        }
                                        onKeyDown={() => {}}
                                        role="tab"
                                        style={{
                                            zIndex,
                                            ...tabInlineStyle,
                                            ...(customActiveTab
                                                ? {
                                                      '--custom-active-tab-bg-color':
                                                          customActiveTab.bg,
                                                      '--custom-active-tab-text-color':
                                                          customActiveTab.text,
                                                  }
                                                : {}),
                                        }}
                                        tabIndex={-1}
                                    >
                                        {(selectedID === item.id && progress) || null}
                                        {item.iconSrc && (
                                            <IconViewSuperEllipse
                                                backgroundColor={iconBackgroundColor}
                                                imageUrl={item.iconSrc}
                                                size={iconSize}
                                            />
                                        )}
                                        {Text ? (
                                            <Text
                                                className={cn(styles.external, styles.text)}
                                                styler={{
                                                    paddingLeft: 4,
                                                    paddingRight: 4,
                                                    paddingTop: isMobile ? 2 : 0,
                                                    paddingBottom: isMobile ? 2 : 0,
                                                }}
                                                tag="text"
                                                view={
                                                    isMobile
                                                        ? 'action-primary-small'
                                                        : 'action-primary-medium'
                                                }
                                            >
                                                {item.title}
                                            </Text>
                                        ) : (
                                            item.title
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </Stack>
            </div>
        </div>
    );
};
